import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { CalendarClock, CheckCircle2, Loader2, Send } from "lucide-react";

const initial = {
  full_name: "",
  mobile: "",
  email: "",
  city: "",
  patient_type: "child",
  age: "",
  preferred_date: "",
  preferred_time: "morning",
  consult_mode: "in_person",
  reports_summary: "",
  consent: false,
};

export default function AppointmentForm() {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please tick the consent box to continue.");
      return;
    }
    if (!/^[6-9]\d{9}$/.test(form.mobile)) {
      toast.error("Please enter a valid 10-digit Indian mobile number.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        age: form.age === "" ? null : parseInt(form.age, 10),
      };
      const { data } = await api.post("/appointment", payload);
      setDone(true);
      toast.success(data.message || "Appointment request received.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="appointment-success"
        className="bg-[#ECFDF5] border border-[#0F766E]/30 rounded-2xl p-8 text-center"
      >
        <span className="inline-flex w-14 h-14 rounded-full bg-[#0F766E] text-white items-center justify-center mb-4">
          <CheckCircle2 size={28} />
        </span>
        <h3 className="font-serif text-2xl font-semibold text-slate-900">Consultation request received</h3>
        <p className="mt-3 text-slate-700 text-[15.5px] leading-relaxed max-w-md mx-auto">
          Our team will confirm your slot with Dr. Sanjay Sachdeva's office within 1 working day.
          Please keep your existing audiogram / hearing reports ready.
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid="appointment-form"
      onSubmit={submit}
      className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 space-y-5"
    >
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-xl bg-[#F0F9FF] flex items-center justify-center text-[#0369A1]">
          <CalendarClock size={20} />
        </span>
        <div>
          <div className="font-serif text-xl font-semibold text-slate-900">
            Request a consultation with Dr. Sachdeva
          </div>
          <div className="text-slate-500 text-[13.5px]">Delhi · Max Healthcare · in-person or video</div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Patient / parent name *">
          <input
            data-testid="appointment-name"
            required
            className="input-hc"
            value={form.full_name}
            onChange={upd("full_name")}
            placeholder="Full name"
          />
        </Field>
        <Field label="Mobile (WhatsApp preferred) *">
          <input
            data-testid="appointment-mobile"
            required
            className="input-hc"
            value={form.mobile}
            onChange={upd("mobile")}
            placeholder="10-digit mobile"
            inputMode="numeric"
            pattern="[0-9]{10}"
          />
        </Field>
        <Field label="Email">
          <input
            data-testid="appointment-email"
            type="email"
            className="input-hc"
            value={form.email}
            onChange={upd("email")}
            placeholder="you@example.com"
          />
        </Field>
        <Field label="City *">
          <input
            data-testid="appointment-city"
            required
            className="input-hc"
            value={form.city}
            onChange={upd("city")}
            placeholder="Where are you based?"
          />
        </Field>

        <Field label="Consultation for *">
          <select data-testid="appointment-patient-type" className="input-hc" value={form.patient_type} onChange={upd("patient_type")}>
            <option value="child">A child</option>
            <option value="adult">An adult</option>
          </select>
        </Field>
        <Field label="Age (years)">
          <input
            data-testid="appointment-age"
            type="number"
            min="0"
            max="120"
            className="input-hc"
            value={form.age}
            onChange={upd("age")}
            placeholder="Optional"
          />
        </Field>

        <Field label="Preferred date">
          <input
            data-testid="appointment-date"
            type="date"
            className="input-hc"
            value={form.preferred_date}
            onChange={upd("preferred_date")}
            min={new Date().toISOString().slice(0, 10)}
          />
        </Field>
        <Field label="Preferred time">
          <select data-testid="appointment-time" className="input-hc" value={form.preferred_time} onChange={upd("preferred_time")}>
            <option value="morning">Morning (10 AM – 1 PM)</option>
            <option value="afternoon">Afternoon (1 PM – 5 PM)</option>
            <option value="evening">Evening (5 PM – 7 PM)</option>
          </select>
        </Field>

        <Field label="Consult mode *" className="sm:col-span-2">
          <div className="grid grid-cols-2 gap-3">
            {[
              { v: "in_person", l: "In-person (Delhi)" },
              { v: "video", l: "Video consultation" },
            ].map((o) => (
              <label
                key={o.v}
                className={`flex items-center gap-2 rounded-xl border px-4 py-3 cursor-pointer text-[14.5px] transition ${
                  form.consult_mode === o.v
                    ? "border-[#0369A1] bg-[#F0F9FF] text-[#0369A1] font-semibold"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <input
                  type="radio"
                  name="consult_mode"
                  value={o.v}
                  checked={form.consult_mode === o.v}
                  onChange={upd("consult_mode")}
                  className="accent-[#0369A1]"
                  data-testid={`appointment-mode-${o.v}`}
                />
                {o.l}
              </label>
            ))}
          </div>
        </Field>

        <Field label="Reports you already have (audiogram, MRI, BERA, ABR, etc.)" className="sm:col-span-2">
          <textarea
            data-testid="appointment-reports"
            className="input-hc min-h-[92px]"
            value={form.reports_summary}
            onChange={upd("reports_summary")}
            placeholder="Brief note — e.g. 'PTA done last month, BERA pending, using bilateral hearing aids since age 2.'"
          />
        </Field>
      </div>

      <label className="flex items-start gap-3 text-[13.5px] text-slate-600 leading-relaxed">
        <input
          data-testid="appointment-consent"
          type="checkbox"
          checked={form.consent}
          onChange={upd("consent")}
          className="mt-0.5 accent-[#0369A1] w-4 h-4"
        />
        I consent to HearClear contacting me by call/WhatsApp/email to coordinate this consultation
        with Dr. Sanjay Sachdeva's office and to share basic case details securely.
      </label>

      <button
        data-testid="appointment-submit"
        type="submit"
        disabled={busy}
        className="btn-primary w-full sm:w-auto"
      >
        {busy ? <><Loader2 size={18} className="animate-spin mr-2" /> Sending…</> : <><Send size={18} className="mr-2" /> Request consultation</>}
      </button>
      <p className="text-[12.5px] text-slate-500">
        This is a request — not a confirmed appointment. Our counsellor will call to confirm the exact slot.
      </p>
    </form>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-[13px] font-semibold text-slate-700 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
